import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { changePassword } from "@/api/auth";
import { APP_ROUTES } from "@/api/constants";

type Payload = {
  oldPassword: string;
  newPassword: string;
};

export function useChangePassword() {
  // `submitting` = we're in the freeze / redirect phase after success
  const [submitting, setSubmitting] = useState(false);
  const [redirectSeconds, setRedirectSeconds] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (payload: Payload) => {
      // Fresh attempt → clear any previous redirect state
      setSubmitting(false);
      setRedirectSeconds(null);

      try {
        // Call backend (no overlay yet, just normal request)
        await changePassword({
          currentPassword: payload.oldPassword,
          newPassword: payload.newPassword,
          confirmNewPassword: payload.newPassword,
        });

        // ✅ Success: start freeze + countdown
        setSubmitting(true);
        setRedirectSeconds(5);

        let current = 5;

        const toastId = toast.success(
          `Password changed successfully. You will be redirected to login in ${current}s…`,
          {
            autoClose: false, // we manage closing manually
          }
        );

        const interval = setInterval(() => {
          current -= 1;

          if (current > 0) {
            setRedirectSeconds(current);
            toast.update(toastId, {
              render: `Password changed successfully. You will be redirected to login in ${current}s…`,
            });
          }
        }, 1000);

        setTimeout(() => {
          clearInterval(interval);
          toast.dismiss(toastId);
          // Final redirect
          navigate(APP_ROUTES.login, { replace: true });
        }, 5000);
      } catch (err: any) {
        // ❌ Error: no freeze, no countdown
        setSubmitting(false);
        setRedirectSeconds(null);
        // Let the form show the top banner
        throw err;
      }
    },
    [navigate]
  );

  return {
    submitting,
    redirectSeconds,
    handleSubmit,
  };
}
